import { useEffect, useRef, useState, useCallback } from 'react';

// PinchZoomWrapper — adds 2-finger pinch zoom + 1-finger pan when zoomed,
// for mobile/touch devices only. Plays nicely with absolutely-positioned
// children (e.g. map hotspots): image and hotspots both transform together
// because they're inside the same transformed wrapper.
//
// Behavior:
//   • 1 finger and scale === 1 → no interception → page scrolls normally
//   • 2 fingers              → pinch zoom (center-anchored)
//   • 1 finger and scale > 1 → pan
//   • Double-tap             → reset to 1× and origin
//
// `disabled` prop (e.g. admin edit mode) bypasses all gestures so the
// underlying drag-to-reposition still works.
//
// Mouse / desktop are intentionally untouched.
export default function PinchZoomWrapper({
  children,
  disabled = false,
  minScale = 1,
  maxScale = 4,
  className = '',
  testId = 'pinch-zoom',
}) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // Mutable refs for active gesture state — avoid setState churn during
  // touchmove to keep the transform smooth at 60fps.
  const gesture = useRef({
    mode: 'none',          // 'none' | 'pinch' | 'pan'
    startDist: 0,
    startScale: 1,
    startTx: 0,
    startTy: 0,
    startCx: 0,            // pinch center (in container px)
    startCy: 0,
    panStartX: 0,          // 1-finger pan start (touch position in container)
    panStartY: 0,
    lastTap: 0,            // ms timestamp for double-tap detection
  });

  const clamp = useCallback((s) => Math.min(maxScale, Math.max(minScale, s)), [minScale, maxScale]);

  const clampTranslate = useCallback((nx, ny, s) => {
    // Don't let the user drag the image past its own edges.
    const el = containerRef.current;
    if (!el) return [nx, ny];
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    // With transform-origin top-left, the visible content spans [tx, tx + w*s]
    // horizontally. Keep that fully inside [0, w] when s > 1, exactly 0 when s = 1.
    const maxTx = 0;
    const minTx = w - w * s;
    const maxTy = 0;
    const minTy = h - h * s;
    return [
      Math.min(maxTx, Math.max(minTx, nx)),
      Math.min(maxTy, Math.max(minTy, ny)),
    ];
  }, []);

  const reset = useCallback(() => {
    setScale(1); setTx(0); setTy(0);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || disabled) return;

    const localPos = (touch) => {
      const r = el.getBoundingClientRect();
      return [touch.clientX - r.left, touch.clientY - r.top];
    };

    const handleStart = (e) => {
      const g = gesture.current;
      if (e.touches.length === 2) {
        const [x1, y1] = localPos(e.touches[0]);
        const [x2, y2] = localPos(e.touches[1]);
        const dx = x2 - x1;
        const dy = y2 - y1;
        g.mode = 'pinch';
        g.startDist = Math.hypot(dx, dy) || 1;
        g.startScale = scale;
        g.startTx = tx;
        g.startTy = ty;
        g.startCx = (x1 + x2) / 2;
        g.startCy = (y1 + y2) / 2;
        e.preventDefault();
      } else if (e.touches.length === 1 && scale > 1) {
        const [x, y] = localPos(e.touches[0]);
        g.mode = 'pan';
        g.panStartX = x - tx;
        g.panStartY = y - ty;
        e.preventDefault();
      } else if (e.touches.length === 1) {
        // Track double-tap for reset (only when zoomed in).
        const now = Date.now();
        if (now - g.lastTap < 300 && scale > 1) {
          reset();
          e.preventDefault();
        }
        g.lastTap = now;
      }
    };

    const handleMove = (e) => {
      const g = gesture.current;
      if (g.mode === 'pinch' && e.touches.length === 2) {
        const [x1, y1] = localPos(e.touches[0]);
        const [x2, y2] = localPos(e.touches[1]);
        const dist = Math.hypot(x2 - x1, y2 - y1) || 1;
        const ratio = dist / g.startDist;
        const nextScale = clamp(g.startScale * ratio);
        // Keep the pinch center stationary on the image while zooming.
        // newTx + cx*nextScale === startTx + cx*startScale
        const nextTx = g.startTx + g.startCx * (g.startScale - nextScale);
        const nextTy = g.startTy + g.startCy * (g.startScale - nextScale);
        const [cx, cy] = clampTranslate(nextTx, nextTy, nextScale);
        setScale(nextScale); setTx(cx); setTy(cy);
        e.preventDefault();
      } else if (g.mode === 'pan' && e.touches.length === 1) {
        const [x, y] = localPos(e.touches[0]);
        const [cx, cy] = clampTranslate(x - g.panStartX, y - g.panStartY, scale);
        setTx(cx); setTy(cy);
        e.preventDefault();
      }
    };

    const handleEnd = () => {
      const g = gesture.current;
      g.mode = 'none';
    };

    el.addEventListener('touchstart', handleStart, { passive: false });
    el.addEventListener('touchmove', handleMove, { passive: false });
    el.addEventListener('touchend', handleEnd);
    el.addEventListener('touchcancel', handleEnd);
    return () => {
      el.removeEventListener('touchstart', handleStart);
      el.removeEventListener('touchmove', handleMove);
      el.removeEventListener('touchend', handleEnd);
      el.removeEventListener('touchcancel', handleEnd);
    };
  }, [disabled, scale, tx, ty, clamp, clampTranslate, reset]);

  // Reset transform if disabled becomes true (e.g. entering edit mode).
  useEffect(() => {
    if (disabled) reset();
  }, [disabled, reset]);

  // touch-action: only block native gestures while zoomed. When scale === 1
  // the page scrolls normally with one finger.
  const touchAction = (disabled || scale === 1) ? 'pan-y pinch-zoom' : 'none';

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        touchAction,
      }}
    >
      <div
        style={{
          width: '100%',
          transformOrigin: '0 0',
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transition: gesture.current.mode === 'none' ? 'transform 180ms ease-out' : 'none',
        }}
      >
        {children}
      </div>
      {scale > 1 && !disabled && (
        <button
          type="button"
          onClick={reset}
          data-testid={`${testId}-reset`}
          aria-label="Reset zoom"
          className="absolute bottom-3 right-3 z-30 px-3 py-1.5 rounded-full text-[11px] font-bold shadow-lg"
          style={{ backgroundColor: 'rgba(0,61,165,0.92)', color: 'white', fontFamily: 'Manrope, sans-serif' }}
        >
          Reset zoom · {scale.toFixed(1)}×
        </button>
      )}
    </div>
  );
}
